// Phase 5: i18next client configuration.
// Loads 9 languages (en/fr/ar/aa/am/so/om/sw/ti) with RTL for Arabic.
// Language is stored in the i18n store (persisted to localStorage) and
// synced to the <html lang/dir> attributes via the I18nGate component.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "@/locales/en.json";
import fr from "@/locales/fr.json";
import ar from "@/locales/ar.json";
import aa from "@/locales/aa.json";
import am from "@/locales/am.json";
import so from "@/locales/so.json";
import om from "@/locales/om.json";
import sw from "@/locales/sw.json";
import ti from "@/locales/ti.json";

export const LANGUAGES = [
  { code: "en", label: "English", nativeLabel: "English", rtl: false, flag: "🇬🇧" },
  { code: "fr", label: "Français", nativeLabel: "Français", rtl: false, flag: "🇫🇷" },
  { code: "ar", label: "العربية", nativeLabel: "العربية", rtl: true, flag: "🇸🇦" },
  { code: "aa", label: "Qafar", nativeLabel: "Qafar af", rtl: false, flag: "🇩🇯" },
  { code: "am", label: "አማርኛ", nativeLabel: "አማርኛ", rtl: false, flag: "🇪🇹" },
  { code: "so", label: "Soomaali", nativeLabel: "Soomaali", rtl: false, flag: "🇸🇴" },
  { code: "om", label: "Afaan Oromoo", nativeLabel: "Afaan Oromoo", rtl: false, flag: "🇪🇹" },
  { code: "sw", label: "Kiswahili", nativeLabel: "Kiswahili", rtl: false, flag: "🇰🇪" },
  { code: "ti", label: "ትግርኛ", nativeLabel: "ትግርኛ", rtl: false, flag: "🇪🇷" },
] as const;

export type LanguageCode = (typeof LANGUAGES)[number]["code"];

export const RTL_LANGUAGES = LANGUAGES.filter((l) => l.rtl).map((l) => l.code);
export const isRtl = (code: string) => RTL_LANGUAGES.includes(code as any);

const STORAGE_KEY = "horndeliver.lang";

function readStoredLang(): string {
  if (typeof window === "undefined") return "en";
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    if (v && LANGUAGES.some((l) => l.code === v)) return v;
  } catch {}
  return "en";
}

if (!i18n.isInitialized) {
  i18n.use(initReactI18next).init({
    resources: {
      en: { translation: en },
      fr: { translation: fr },
      ar: { translation: ar },
      aa: { translation: aa },
      am: { translation: am },
      so: { translation: so },
      om: { translation: om },
      sw: { translation: sw },
      ti: { translation: ti },
    },
    lng: readStoredLang(),
    fallbackLng: "en",
    interpolation: {
      // React already escapes by default.
      escapeValue: false,
    },
    returnNull: false,
  });
}

// Persist + reflect lang/dir whenever the language changes.
i18n.on("languageChanged", (lng: string) => {
  if (typeof window !== "undefined") {
    try {
      window.localStorage.setItem(STORAGE_KEY, lng);
    } catch {}
    const dir = isRtl(lng) ? "rtl" : "ltr";
    document.documentElement.lang = lng;
    document.documentElement.dir = dir;
  }
});

export default i18n;
